import { call, delay, put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import { MODEL_VIEW } from 'src/store/ActionTypes';
import CONSTANTS from 'src/store/constants';

import * as api from 'src/store/api';
import { calcEqPersonsAdj, calcEqPremiumsAdj, identity } from 'src/functions';
import get from 'lodash.get';
import set from 'lodash.set';
import { correctMeanPremiumPerPolicy, simplifyTypeDetail } from 'src/functions/actuarial_calculator';

/**
 *
 * @return {Model}
 */
const selectModel = (state) => state.modelView.model;

/**
 *
 * @return {RoiCalcState}
 */
const selectRoiCalcState = (state) => state.modelView.roiCalculatorState;

const selectAvailableQuantiles = (state) => state.modelView.availableQuantiles;
const selectQuantiles = (state) => state.modelView.quantiles;

function* fetchModel(action) {
  try {
    const model = yield call(api.fetchModel, action.payload);
    yield put({
      type: MODEL_VIEW.FETCH_MODEL_SUCCEEDED,
      payload: model,
    });
    const segmentId = model?.modelData?.dataPayload?.actuarial_segment_id;
    const isRoiCalcPage = window.location.pathname.includes('roi');
    if (segmentId && isRoiCalcPage) {
      const segmentOptions = {
        headers: {
          'X-Fields': 'id,datasetId',
        },
      };
      const segment = yield call(api.fetchSegment, segmentId, segmentOptions);
      const datasetOptions = {
        headers: {
          'X-Fields': 'id,payload{roi_calculator_seed}',
        },
      };
      const dataset = yield call(api.fetchDataset, segment.datasetId, datasetOptions);
      yield put({
        type: MODEL_VIEW.SET_ROI_CALC_SEED_FROM_DATASET,
        payload: dataset,
      });
    }
  } catch (e) {
    yield put({
      type: MODEL_VIEW.FETCH_MODEL_FAILED,
    });
  }
}

function* remediateModel(action) {
  try {
    const { modelId, treatment, lowerBound, upperBound } = action.payload;
    yield call(api.addThinkalikeModelStatus, modelId, {
      status: 'MODEL_REMEDIATION_PENDING',
      payload: {
        treatment_type: treatment,
        lower_bound: lowerBound,
        upper_bound: upperBound,
      },
    });
    yield put({
      type: MODEL_VIEW.REMEDIATE_MODEL_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: MODEL_VIEW.REMEDIATE_MODEL_FAILED,
    });
  }
}

function* fetchInputDistDownloadUrl(action) {
  const { entityId, entityType } = action.payload;
  const matchType = entityType === 'dataset' ? 'pre' : 'post';
  try {
    const url = yield call(api.fetchModelInputDistributionDownloadUrl, entityId, entityType);
    yield put({
      type: MODEL_VIEW.FETCH_INPUT_DIST_DOWNLOAD_URL_SUCCEEDED,
      payload: { url, matchType },
    });
  } catch (e) {
    yield put({
      type: MODEL_VIEW.FETCH_INPUT_DIST_DOWNLOAD_URL_FAILED,
      payload: { error: e.message || e.toString(), matchType },
    });
  } finally {
    delay(500);
    entityType === 'dataset'
      ? yield put({
          type: MODEL_VIEW.UNSET,
          payload: 'fetchPreInputDistDownloadUrlStatus',
        })
      : yield put({
          type: MODEL_VIEW.UNSET,
          payload: 'fetchPostInputDistDownloadUrlStatus',
        });
  }
}

function* fetchLiftChartDownloadUrl(action) {
  const { entityId } = action.payload;
  try {
    const url = yield call(api.fetchModelLiftChartDownloadUrl, entityId);
    yield put({
      type: MODEL_VIEW.FETCH_LIFT_CHART_DOWNLOAD_URL_SUCCEEDED,
      payload: { url },
    });
  } catch (e) {
    yield put({
      type: MODEL_VIEW.FETCH_LIFT_CHART_DOWNLOAD_URL_FAILED,
      payload: { error: e.message || e.toString() },
    });
  } finally {
    delay(500);
    yield put({
      type: MODEL_VIEW.UNSET,
      payload: 'fetchLiftChartDownloadUrlStatus',
    });
  }
}

function* fetchDownloadUrl(action) {
  try {
    const downloadUrl = yield call(api.fetchModelScoresDownloadUrl, action.payload);
    yield put({
      type: MODEL_VIEW.FETCH_DOWNLOAD_URL_SUCCEEDED,
      payload: downloadUrl,
    });
  } catch (e) {
    yield put({
      type: MODEL_VIEW.FETCH_DOWNLOAD_URL_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: MODEL_VIEW.UNSET,
      payload: 'fetchDownloadUrlStatus',
    });
  }
}

/**
 * extractQuantileInfo - extract quantile info from the chart
 * @param {{type: string, payload: Object }} action
 * @return {Generator<*, void, *>}
 */
function* extractQuantileInfo(action) {
  try {
    const model = action.payload;
    const modelType = model?.dataModelType;
    const modelVersion = model?.payload?.version;
    const isLegacyActuarial = ['LIFT_VERSION_1_ACT', null, undefined].includes(modelVersion);
    const availableQuantiles = {};
    const defaultQuantile =
      CONSTANTS.TAL_MODEL.DEFAULT_QUANTILE?.[modelVersion] ?? CONSTANTS.TAL_MODEL.DEFAULT_QUANTILE.DEFAULT;
    if (modelType === CONSTANTS.TAL_MODEL.TAG.CLASSIFIER || isLegacyActuarial) {
      const liftScores = get(
        model,
        'modelData.dataPayload.lift_scores',
        get(model, 'modelData.dataPayload.liftScores', [])
      );
      availableQuantiles.current = liftScores.map((data) => data?.percentile?.length).filter(identity);
    } else {
      // actuarial models versions 2, 3
      const query = new URLSearchParams(window.location.search);
      const equal = query.get('equal') ?? 'persons';
      const equalPersonsLiftScores = model?.modelData?.dataPayload?.equal_persons?.lift_scores;
      const equalPremiumsLiftScores =
        model?.modelData?.dataPayload?.equal_premiums?.lift_scores ??
        model?.modelData?.dataPayload?.equal_premium?.lift_scores;
      if (equalPremiumsLiftScores) {
        availableQuantiles.premiums = equalPremiumsLiftScores.map((data) => data?.percentile?.length).filter(identity);
      }
      availableQuantiles.persons = equalPersonsLiftScores.map((data) => data?.percentile?.length).filter(identity);
      availableQuantiles.current = [...(availableQuantiles[equal] ?? availableQuantiles.persons)];
    }
    yield put({
      type: MODEL_VIEW.SET_AVAILABLE_QUANTILES,
      payload: availableQuantiles,
    });
    yield put({
      type: MODEL_VIEW.SET_QUANTILES,
      payload: defaultQuantile,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

/**
 * updateQuantileInfo - update quantile info from the chart
 * @param {{type: string, payload: string }} action
 * @return {Generator<*, void, *>}
 */
function* updateQuantileInfo(action) {
  try {
    const model = yield select(selectModel);
    const modelVersion = model?.payload?.version;
    const nextEqualType = action.payload;
    const defaultQuantile =
      CONSTANTS.TAL_MODEL.DEFAULT_QUANTILE?.[modelVersion] ?? CONSTANTS.TAL_MODEL.DEFAULT_QUANTILE.DEFAULT;
    const availableQuantiles = yield select(selectAvailableQuantiles);
    let quantiles = yield select(selectQuantiles);
    let quantileIdx = availableQuantiles[nextEqualType].findIndex((qn) => qn === quantiles);
    const updateQuantiles = quantileIdx === -1;
    if (updateQuantiles) {
      quantileIdx = availableQuantiles[nextEqualType].findIndex((qn) => qn === defaultQuantile);
      quantiles = defaultQuantile;
    }
    yield put({
      type: MODEL_VIEW.SET_AVAILABLE_QUANTILES,
      payload: {
        ...availableQuantiles,
        current: JSON.parse(JSON.stringify(availableQuantiles[nextEqualType])),
      },
    });
    yield put({
      type: MODEL_VIEW.SET_QUANTILE_IDX,
      payload: quantileIdx,
    });
    if (updateQuantiles) {
      yield put({
        type: MODEL_VIEW.SET_QUANTILES,
        payload: 10,
      });
    } else {
      yield put({
        type: MODEL_VIEW.SET_QUANTILES,
        payload: quantiles,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* saveRoiCalculatorState(action) {
  try {
    yield call(api.saveRoiCalculatorState, action.payload);
    yield put({
      type: MODEL_VIEW.SAVE_ROI_CALCULATOR_STATE_SUCCEEDED,
    });
  } catch (e) {
    yield put({
      type: MODEL_VIEW.SAVE_ROI_CALCULATOR_STATE_FAILED,
      payload: e.message || e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: MODEL_VIEW.UNSET,
      payload: 'saveRoiCalculatorStateStatus',
    });
  }
}

/**
 * exportRoiCalculator - flow control for export of an roi calculator
 * @returns {Generator<*, void, *>}
 */
function* exportRoiCalculator() {
  try {
    const calcData = {};
    const model = yield select(selectModel);
    const roiCalculatorState = yield select(selectRoiCalcState);
    const simpleType = simplifyTypeDetail(model.typeDetail);
    const isPersonalAuto = (model?.typeDetail ?? '').includes('PERSONAL AUTO');
    const dataModelType = model?.dataModelType;
    const modelVersion = model?.payload?.version;
    const isLegacy = ['LIFT_VERSION_1_ACT', null, undefined].includes(modelVersion);
    if (dataModelType === 'CLASSIFIER') return;
    let liftScores;
    let liftScore;
    let equalType = null;
    if (isLegacy) {
      liftScores = model?.modelData?.dataPayload?.lift_scores;
      liftScore = liftScores.find((info) => info.p_lift.length === 10);
    } else {
      equalType = new URL(window.location.href).searchParams.get('equal');
      liftScores = model?.modelData?.dataPayload?.[`equal_${equalType}`]?.lift_scores;
      liftScore = liftScores.find((info) => info.p_lift.length === 10);
    }
    const { pinpointPIFPercentile, pinpointLosses, pinpointPremiums } =
      equalType === 'persons'
        ? calcEqPersonsAdj(
            get(liftScore, 'p_pif_percentile', []),
            get(liftScore, 'p_risk_cost', []),
            get(liftScore, 'p_premium', [])
          )
        : calcEqPremiumsAdj(
            get(liftScore, 'p_pif_percentile', []),
            get(liftScore, 'p_risk_cost', []),
            get(liftScore, 'p_premium', [])
          );
    calcData.simpleType = simpleType;
    calcData.simpleModel = CONSTANTS.TAL_MODEL.TAG_SMP_DESC[model.dataModelType];
    calcData.riskModel = dataModelType;
    calcData.riskModelDetail = model?.typeDetail;
    calcData.pinpointLift = liftScore.p_lift;
    calcData.baselineLift = liftScore.b_lift;
    calcData.acquisitionDistribution = roiCalculatorState?.acquisitionDistribution;
    calcData.nonRenewalDistribution = roiCalculatorState?.nonRenewalDistribution;
    calcData.acquisitionRate = roiCalculatorState?.acquisitionRate;
    calcData.averagePremium = roiCalculatorState?.averagePremium;
    calcData.carrierNonRenewalRate = roiCalculatorState?.carrierNonRenewalRate;
    calcData.hasBaselineModel = roiCalculatorState?.hasBaselineModel;
    calcData.policiesInForce = roiCalculatorState?.policiesInForce;
    calcData.riskCost = roiCalculatorState?.riskCost;
    calcData.yearlyCancellationRate = roiCalculatorState?.yearlyCancellationRate;
    calcData.baselineLosses = liftScore?.b_risk_cost;
    calcData.baselinePremiums = liftScore?.b_premium;
    calcData.baselinePIFPercentile = liftScore?.b_pif_percentile;
    calcData.pinpointLosses = pinpointLosses;
    calcData.pinpointPremiums = pinpointPremiums;
    calcData.pinpointPIFPercentile = pinpointPIFPercentile;
    calcData.avgPremium = correctMeanPremiumPerPolicy(liftScore?.avg_premium, calcData.averagePremium, isPersonalAuto);
    if (equalType) calcData.equalType = equalType;
    calcData.yearlyRates = roiCalculatorState?.yearlyRates;
    const data = yield call(api.exportRoiCalculator, calcData);
    const sliceSize = 512;
    const byteCharacters = atob(data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const fout = new File(byteArrays, 'roi_calculator_export.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    window.open(URL.createObjectURL(fout), '_blank');
  } catch (e) {
    yield put({
      type: MODEL_VIEW.SAVE_ROI_CALCULATOR_STATE_FAILED,
      payload: e.toString(),
    });
  } finally {
    delay(500);
    yield put({
      type: MODEL_VIEW.UNSET,
      payload: 'exportRoiCalculatorStatus',
    });
  }
}

function* updateRoiCalculatorState(action) {
  try {
    const { key, value } = action.payload;
    const calculatorState = yield select((state) => state.modelView.roiCalculatorState);
    if (
      key.includes('baselineLift') ||
      key.includes('combinedLift') ||
      key.includes('acquisitionDistribution') ||
      key.includes('nonRenewalDistribution') ||
      key.includes('yearlyRates')
    ) {
      const [type] = key.split('.');
      calculatorState[type] = JSON.parse(JSON.stringify(calculatorState[type]));
      set(calculatorState, key, +value);
    } else {
      calculatorState[key] = value;
    }
    yield put({
      type: MODEL_VIEW.EXPORT_ROI_CALCULATOR_READY,
      payload: false,
    });
    yield put({
      type: MODEL_VIEW.UPDATE_ROI_CALCULATOR_STATE_SUCCEEDED,
      payload: calculatorState,
    });
  } catch (e) {
    yield put({
      type: MODEL_VIEW.UPDATE_ROI_CALCULATOR_STATE_FAILED,
      payload: e.message || e.toString(),
    });
  }
}

function* updateRoiCalculatorStateSaga() {
  yield takeEvery(MODEL_VIEW.UPDATE_ROI_CALCULATOR_STATE, updateRoiCalculatorState);
}

function* saveRoiCalculatorStateSaga() {
  yield takeLatest(MODEL_VIEW.SAVE_ROI_CALCULATOR_STATE, saveRoiCalculatorState);
}

function* exportRoiCalculatorSaga() {
  yield takeLatest(MODEL_VIEW.EXPORT_ROI_CALCULATOR, exportRoiCalculator);
}

function* fetchModelSaga() {
  yield takeLatest(MODEL_VIEW.FETCH_MODEL, fetchModel);
}

function* remediateModelSaga() {
  yield takeLatest(MODEL_VIEW.REMEDIATE_MODEL, remediateModel);
}

function* extractQuantileInfoSaga() {
  yield takeLatest(MODEL_VIEW.FETCH_MODEL_SUCCEEDED, extractQuantileInfo);
}

function* updateQuantileInfoSaga() {
  yield takeLatest(MODEL_VIEW.SET_EQUAL_TYPE, updateQuantileInfo);
}

function* fetchInputDistDownloadUrlSaga() {
  yield takeEvery(MODEL_VIEW.FETCH_INPUT_DIST_DOWNLOAD_URL, fetchInputDistDownloadUrl);
}

function* fetchLiftChartDownloadUrlSaga() {
  yield takeEvery(MODEL_VIEW.FETCH_LIFT_CHART_DOWNLOAD_URL, fetchLiftChartDownloadUrl);
}

function* fetchDownloadUrlSaga() {
  yield takeLatest(MODEL_VIEW.FETCH_DOWNLOAD_URL, fetchDownloadUrl);
}

export default [
  fetchModelSaga(),
  fetchDownloadUrlSaga(),
  fetchInputDistDownloadUrlSaga(),
  fetchLiftChartDownloadUrlSaga(),
  remediateModelSaga(),
  updateQuantileInfoSaga(),
  extractQuantileInfoSaga(),
  saveRoiCalculatorStateSaga(),
  updateRoiCalculatorStateSaga(),
  exportRoiCalculatorSaga(),
];
